import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../state/auth';
import { useBoard } from '../state/board';
import { getTheme } from '../themes';
import type { Board } from '@blackboard/shared';
import { BoardIcon, CheckIcon, ChevronDownIcon, SettingsIcon } from './icons';

export function BoardSwitcher() {
  const { slug } = useParams();
  const token = useAuth((s) => s.token);
  const user = useAuth((s) => s.user);
  const currentBoard = useBoard((s) => s.board);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [boards, setBoards] = useState<(Board & { online: number; last_stroke_at: string | null })[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);

  // Panel colors follow the *current* board's theme — so controls don't fight the canvas
  const theme = currentBoard ? getTheme(currentBoard.theme) : null;

  useEffect(() => {
    if (!token) return;
    fetch('/api/boards', { headers: { authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setBoards)
      .catch(() => {});
  }, [token, slug]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = boards.find((b) => b.slug === slug);
  if (!theme) return null;

  const surfaceStyle: React.CSSProperties = {
    background: theme.surface,
    color: theme.surfaceText,
    border: `1px solid ${theme.surfaceBorder}`,
    boxShadow: theme.surfaceShadow,
  };

  return (
    <div ref={rootRef} className="absolute top-4 left-4 z-30">
      <button
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 h-10 pl-3 pr-2.5 rounded-xl backdrop-blur-xl text-sm font-medium"
        style={surfaceStyle}
      >
        <BoardIcon size={16} />
        <span className="max-w-[180px] truncate">{current?.name ?? slug}</span>
        <ChevronDownIcon
          size={14}
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 180ms ease',
            opacity: 0.7,
          }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="切换画板"
          className="bb-fade-in mt-2 rounded-xl overflow-hidden backdrop-blur-xl min-w-[260px]"
          style={surfaceStyle}
        >
          <div
            className="px-3 py-2 text-[11px] uppercase tracking-wider"
            style={{ color: theme.surfaceTextMuted }}
          >
            所有画板
          </div>
          <div className="max-h-72 overflow-y-auto">
            {boards.map((b) => {
              const isCurrent = b.slug === slug;
              return (
                <button
                  key={b.id}
                  role="option"
                  aria-selected={isCurrent}
                  onClick={() => {
                    nav(`/b/${b.slug}`);
                    setOpen(false);
                  }}
                  className="w-full text-left flex items-center gap-3 px-3 py-2"
                  style={{ background: isCurrent ? theme.selectedBg : 'transparent' }}
                  onMouseEnter={(e) => {
                    if (!isCurrent) e.currentTarget.style.background = theme.hoverBg;
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span
                    className="flex-none w-5 h-5 flex items-center justify-center"
                    style={{ color: isCurrent ? theme.selectedRing : 'transparent' }}
                  >
                    <CheckIcon size={14} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{b.name}</span>
                    <span
                      className="block text-[11px] truncate font-mono"
                      style={{ color: theme.surfaceTextMuted }}
                    >
                      /{b.slug}
                      {b.last_stroke_at && (
                        <span className="ml-2 font-sans">· {formatRelative(b.last_stroke_at)}</span>
                      )}
                    </span>
                  </span>
                  <OnlinePill count={b.online} theme={theme} />
                </button>
              );
            })}
            {boards.length === 0 && (
              <div
                className="px-3 py-6 text-sm text-center"
                style={{ color: theme.surfaceTextMuted }}
              >
                暂无画板
              </div>
            )}
          </div>
          {user?.is_admin && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-t"
              style={{
                borderColor: theme.divider,
                color: theme.accent,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = theme.hoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <SettingsIcon size={16} />
              <span>管理员面板</span>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// "5 分钟前" / "刚刚" — quick-glance freshness so users pick alive boards.
// Domain D2-3 围观心跳感 last-activity dimension.
function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 60) return '刚刚';
  if (sec < 3600) return `${Math.floor(sec / 60)} 分钟前`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} 小时前`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)} 天前`;
  if (sec < 86400 * 30) return `${Math.floor(sec / 86400 / 7)} 周前`;
  return `${Math.floor(sec / 86400 / 30)} 个月前`;
}

function OnlinePill({
  count,
  theme,
}: {
  count: number;
  theme: ReturnType<typeof getTheme>;
}) {
  const active = count > 0;
  return (
    <span
      className="flex items-center gap-1.5 text-[11px] font-medium px-1.5 py-0.5 rounded-md"
      style={{
        color: active ? theme.accent : theme.surfaceTextMuted,
        background: active ? theme.selectedBg : 'transparent',
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: active ? theme.accent : theme.surfaceTextMuted }}
      />
      {count}
    </span>
  );
}
